import { useEffect, useCallback, useRef } from 'react'
import toast from 'react-hot-toast'
import { Sparkles, X } from 'lucide-react'
import { apiService } from '@/services/api'
import { APP_VERSION } from '@/version'

const STORAGE_KEY_DISMISSED = 'app_dismissed_version'
const CHECK_INTERVAL = 1800000 // 30 minutes in milliseconds

/**
 * Custom hook for checking and notifying users of new app versions
 * Polls the /health endpoint every 30 minutes and shows a toast when a new version is detected
 */
export function useVersionCheck(enabled = true) {
  const toastIdRef = useRef<string | null>(null)

  /**
   * Check if there's a new version available
   */
  const checkVersion = useCallback(async () => {
    try {
      const { version: serverVersion } = await apiService.getVersion()
      const dismissedVersion = localStorage.getItem(STORAGE_KEY_DISMISSED)

      console.log('🔍 [VersionCheck] Checking for updates', {
        clientVersion: APP_VERSION,
        serverVersion,
        dismissedVersion
      })

      // If server version is different from client version AND hasn't been dismissed
      if (serverVersion !== APP_VERSION && serverVersion !== dismissedVersion) {
        console.log('🎉 [VersionCheck] New version detected!', {
          currentVersion: APP_VERSION,
          newVersion: serverVersion
        })

        // Show persistent toast with Refresh button
        showUpdateNotification(serverVersion)
      }
    } catch (error) {
      console.error('❌ [VersionCheck] Failed to check version:', error)
      // Silently fail - don't interrupt user experience
    }
  }, [])

  /**
   * Show update notification
   */
  const showUpdateNotification = useCallback((newVersion: string) => {
    // Dismiss existing notification if any
    if (toastIdRef.current) {
      toast.dismiss(toastIdRef.current)
    }

    // Create persistent toast notification with custom UI
    const toastId = toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? 'animate-enter' : 'animate-leave'
          } pointer-events-auto w-[calc(100vw-2rem)] max-w-xs rounded-xl border border-border bg-card/95 text-card-foreground shadow-lg backdrop-blur`}
        >
          <div className="flex items-center gap-3 p-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Sparkles className="h-4 w-4 text-primary" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold leading-5 text-foreground">
                Update ready
              </p>
              <p className="truncate text-xs leading-5 text-muted-foreground">
                Refresh for the newest SuperSchema.
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button
                onClick={() => {
                  // Mark this version as seen before reloading
                  localStorage.setItem(STORAGE_KEY_DISMISSED, newVersion)
                  // Force hard reload to bypass cache
                  window.location.reload()
                }}
                className="inline-flex h-8 items-center justify-center rounded-full bg-primary px-3 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                Refresh
              </button>
              <button
                onClick={() => {
                  localStorage.setItem(STORAGE_KEY_DISMISSED, newVersion)
                  toast.dismiss(toastId)
                  toastIdRef.current = null
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <span className="sr-only">Dismiss</span>
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ),
      {
        duration: Infinity, // Persistent until user dismisses
        position: 'bottom-left',
      }
    )

    toastIdRef.current = toastId
  }, [])

  /**
   * Set up polling interval
   */
  useEffect(() => {
    if (!enabled) {
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current)
        toastIdRef.current = null
      }
      return
    }

    // Check immediately on mount
    checkVersion()

    // Set up interval to check every 30 minutes
    const intervalId = setInterval(checkVersion, CHECK_INTERVAL)

    // Cleanup on unmount
    return () => {
      clearInterval(intervalId)
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current)
      }
    }
  }, [checkVersion, enabled])

  return {
    checkVersion // Exposed for manual checks if needed
  }
}
